import { Box, Button, Divider, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import React from 'react';
import { useQuery } from 'react-query';
import { useI18nContext } from '@procore/core-react';
import { DEFAULT_BUSINESSES_FLOOR } from '../../constants';
import { fetchSearch } from '../../data/search';
import { FilterSet, FilterState } from '../../helpers/filters';
import SearchResultsResultsContainer from './SearchResultsResultsContainer';
import { GeoInfo } from '../../services/geo-info';
import { useStyles } from './SearchResultsNoResults.styles';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';

const showRemoveAllFilters = (filters: FilterSet) => {
  return (
    !!filters.companyType.length ||
    !!filters.constructionSector.length ||
    !!filters.trade.length ||
    !!filters.classifications.length
  );
};

const SearchResultsNoResults = ({
  useFilterHandlers,
  location,
}: {
  useFilterHandlers: FilterState;
  location: GeoInfo;
}) => {
  const I18n = useI18nContext();
  const classes = useStyles();
  const router = useRouter();
  const query = router.query.q?.toString();

  const similarResultsQuery = useQuery(
    [
      'similarResultsQuery',
      location,
      query,
      useFilterHandlers.filters,
      undefined,
      DEFAULT_BUSINESSES_FLOOR,
    ],
    () =>
      fetchSearch(
        location,
        query,
        useFilterHandlers.filters,
        undefined,
        DEFAULT_BUSINESSES_FLOOR
      ),
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  );

  return (
    <>
      <Typography className={classes.noResultsSuggestion}>
        {I18n.t('components.search_results_no_results.change')}
      </Typography>
      {showRemoveAllFilters(useFilterHandlers.filters) && (
        <AnalyticsWrappedComponent
          componentType={Button}
          eventText="Clicked Remove All Filters"
          eventLabel="No Search Results"
          data-track-click="Empty Search Results, CTA, Remove all filters"
          onClick={() => {
            useFilterHandlers.clearFilters();
          }}
          variant="contained"
          color="primary"
        >
          {I18n.t('components.search_results_no_results.remove_all_filters')}
        </AnalyticsWrappedComponent>
      )}
      {!!similarResultsQuery.isSuccess && !!similarResultsQuery.data.count && (
        <>
          <Box my={3}>
            <Divider />
          </Box>
          <Box mb={3}>
            <Typography
              data-test-id="similar-results-text"
              component="h2"
              variant="h3"
            >
              {I18n.t('components.search_results_no_results.similar_results', {
                radius: similarResultsQuery.data.finalRadius,
              })}
            </Typography>
          </Box>
          <SearchResultsResultsContainer
            loading={similarResultsQuery.isLoading}
            searchResults={similarResultsQuery.data.results}
          />
        </>
      )}
    </>
  );
};

export default SearchResultsNoResults;
